import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Construction, ArrowRight, Bell, LayoutDashboard } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const DIALOG_COPY = {
  default: {
    title: 'Кабинет наставника почти готов',
    subtitle:
      'Мы активно дорабатываем связку «ученик — репетитор / учитель»: приглашения, задания, аналитика по классу и отчёты. Пока этот раздел закрыт для входа, но скоро откроем доступ.',
    bullets: [
      'Единый кабинет для репетиторов, учителей и родителей',
      'Назначение работ и контроль прогресса без ручной рутины',
      'Привязка учеников по коду и прозрачные заявки',
    ],
  },
  register_role: {
    title: 'Регистрация для наставников — скоро',
    subtitle:
      'Сейчас можно создать аккаунт как ученик и пользоваться практикой, теорией и аналитикой. Отдельные роли репетитора и учителя мы подключаем в ближайшем обновлении — следите за новостями.',
    bullets: [
      'Репетитор и учитель появятся в списке ролей автоматически',
      'Текущие ученики ничего не теряют: их кабинет уже работает',
      'Мы заранее протестируем сценарии приглашений и заданий',
    ],
  },
  register_prep: {
    title: 'Привязка по коду — в разработке',
    subtitle:
      'Режим «с репетитором» или «с учителем» сейчас недоступен: мы доводим до ума безопасную привязку и уведомления. Выберите «Самостоятельно» — весь функционал подготовки для вас уже открыт.',
    bullets: [
      'Полный доступ к заданиям, пробникам и разборам',
      'При появлении наставника вы сможете подключиться в пару кликов',
      'Настройки сохранятся — вернуться к привязке можно будет позже',
    ],
  },
  nav_marketing: {
    title: 'Раздел для наставников в обновлении',
    subtitle:
      'Страницы для репетиторов и учителей остаются с описанием возможностей, но регистрация в этой роли пока закрыта. Загляните на лендинг — там уже можно оценить концепцию продукта.',
    bullets: [
      'Описание сценариев и выгод — на страницах «Репетиторам» и «Учителям»',
      'Регистрация наставника откроется отдельным анонсом',
      'Ученики могут пользоваться платформой без ограничений по этому поводу',
    ],
  },
};

export function EducatorSuiteComingSoonDialog({ open, onOpenChange, variant = 'default' }) {
  const copy = DIALOG_COPY[variant] || DIALOG_COPY.default;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(100vw-1.5rem,26rem)] rounded-[1.75rem] border border-primary/20 bg-background p-0  dark:to-primary/[0.06] sm:max-w-md overflow-hidden gap-0">
        <div className="pointer-events-none absolute -right-20 -top-24 h-56 w-56 rounded-full bg-primary/15 blur-3xl" aria-hidden />
        {/* В светлой теме блоб у нижнего края давал «грязный» фон под полупрозрачным футером — ослабляем только для light */}
        <div
          className="pointer-events-none absolute -left-12 bottom-24 h-36 w-36 rounded-full bg-primary/[0.06] blur-2xl dark:-left-16 dark:bottom-0 dark:h-40 dark:w-40 dark:bg-primary/10"
          aria-hidden
        />
        <DialogHeader className="relative space-y-4 px-6 pt-8 pb-2 text-left">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/12 ring-1 ring-primary/20">
              <Construction className="h-6 w-6 text-primary" aria-hidden />
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              Скоро
            </span>
          </div>
          <DialogTitle className="text-xl font-heading font-semibold leading-snug tracking-tight sm:text-2xl pr-2">
            {copy.title}
          </DialogTitle>
          <DialogDescription className="text-[15px] leading-relaxed text-muted-foreground">
            {copy.subtitle}
          </DialogDescription>
        </DialogHeader>
        <ul className="relative space-y-3 px-6 py-4 text-sm text-foreground/85">
          {copy.bullets.map((line) => (
            <li key={line} className="flex gap-3">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/80" aria-hidden />
              <span className="leading-relaxed">{line}</span>
            </li>
          ))}
        </ul>
        <DialogFooter className="relative z-10 flex-col gap-2 border-t border-border bg-card px-6 py-5 dark:bg-muted/25 sm:flex-col">
          <Button
            type="button"
            className="h-11 w-full rounded-full font-semibold shadow-md shadow-primary/15"
            onClick={() => onOpenChange(false)}
          >
            {variant === 'register_role' || variant === 'register_prep'
              ? 'Понятно'
              : 'Хорошо, жду запуска'}
          </Button>
          {(variant === 'nav_marketing' || variant === 'default') && (
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
              <Button
                type="button"
                variant="ghost"
                className="rounded-full text-foreground/85 hover:bg-primary/10 hover:text-primary dark:text-muted-foreground dark:hover:text-primary"
                asChild
              >
                <Link to="/for-tutors" onClick={() => onOpenChange(false)} className="gap-1 font-medium">
                  Репетиторам
                  <ArrowRight className="h-4 w-4 opacity-80" />
                </Link>
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="rounded-full text-foreground/85 hover:bg-primary/10 hover:text-primary dark:text-muted-foreground dark:hover:text-primary"
                asChild
              >
                <Link to="/for-teachers" onClick={() => onOpenChange(false)} className="gap-1 font-medium">
                  Учителям
                  <ArrowRight className="h-4 w-4 opacity-80" />
                </Link>
              </Button>
            </div>
          )}
          <p className="flex items-center justify-center gap-2 text-center text-xs leading-snug text-foreground/80 dark:text-muted-foreground">
            <Bell className="h-3.5 w-3.5 shrink-0 text-primary dark:text-primary/80" aria-hidden />
            Мы сообщим в приложении и на почте, когда раздел откроется.
          </p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Полноэкранная заглушка для маршрутов /tutor, /teacher, /parent (когда suite выключен).
 */
export function EducatorSuiteComingSoonFullPage() {
  return (
    <div className="page-shell relative min-h-[calc(100dvh-8rem)] overflow-hidden flex flex-col items-center justify-center px-4 py-16">
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-25 dark:opacity-15" aria-hidden />
      <div
        className="pointer-events-none absolute -top-40 left-1/2 h-[28rem] w-[min(100vw,42rem)] -translate-x-1/2 rounded-full bg-primary/[0.12] blur-3xl"
        aria-hidden
      />
      <div className="relative z-10 w-full max-w-lg text-center">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-primary/10 ring-1 ring-primary/25 shadow-lg shadow-primary/10">
          <LayoutDashboard className="h-9 w-9 text-primary" aria-hidden />
        </div>
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-primary">
          <Sparkles className="h-3.5 w-3.5" />
          В разработке
        </div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight sm:text-3xl md:text-4xl">
          Кабинет наставника скоро откроется
        </h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
          Мы собираем финальные сценарии: ученики, коды приглашения, домашние задания и аналитика по группе.
          Сейчас этот раздел специально недоступен — так мы избегаем полурабочих состояний и путаницы.
        </p>
        <ul className="mt-8 space-y-3 text-left text-sm text-foreground/85">
          {[
            'Единый вход для репетитора, учителя и родителя',
            'Прозрачные заявки и статусы привязки учеников',
            'Назначение работ из банка и из пробников',
          ].map((line) => (
            <li key={line} className="flex gap-3 rounded-2xl border border-border/50 bg-background/60 px-4 py-3 backdrop-blur-sm">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
              <span>{line}</span>
            </li>
          ))}
        </ul>
        <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button className="h-12 rounded-full px-8 font-semibold shadow-md shadow-primary/20" asChild>
            <Link to="/dashboard">В ученический кабинет</Link>
          </Button>
          <Button variant="outline" className="h-12 rounded-full px-8 font-semibold border-border/70" asChild>
            <Link to="/settings">Настройки профиля</Link>
          </Button>
        </div>
        <p className="mt-8 text-xs leading-relaxed text-muted-foreground">
          Нужна срочная помощь по аккаунту наставника — напишите в поддержку с темой «Наставник», мы ответим вручную.
        </p>
      </div>
    </div>
  );
}

/**
 * Кнопка в стиле навигации: видна как раньше, по клику — диалог (гости).
 */
export function EducatorSuiteSoftNavButton({
  children,
  className,
  dialogVariant = 'nav_marketing',
  testId,
  onBeforeOpen,
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={() => {
          onBeforeOpen?.();
          setOpen(true);
        }}
        className={cn(className)}
      >
        {children}
      </button>
      <EducatorSuiteComingSoonDialog open={open} onOpenChange={setOpen} variant={dialogVariant} />
    </>
  );
}
